import { useEffect, useState } from 'react';

interface BotDetectionResult {
  isBot: boolean;
  isSuspicious: boolean;
  isReady: boolean;
}

/**
 * Search engines and social preview crawlers must NEVER be blocked — they render
 * with headless Chrome (navigator.webdriver === true), which would otherwise be
 * treated as scraping and de-index our knowledge pages.
 */
const ALLOWED_CRAWLERS =
  /Googlebot|Google-InspectionTool|Storebot-Google|Google-Site-Verification|AdsBot-Google|Bingbot|BingPreview|DuckDuckBot|Baiduspider|YandexBot|Applebot|Slurp|Twitterbot|facebookexternalhit|LinkedInBot|Slackbot|TelegramBot|WhatsApp|Discordbot|Pinterestbot/i;

/** Content-harvesting / AI-training crawlers we do block. */
const TRAINING_BOTS =
  /CCBot|Bytespider|Diffbot|Meta-ExternalAgent|cohere-ai|ImagesiftBot|GPTBot|ClaudeBot|Claude-Web|anthropic-ai|PerplexityBot|Omgilibot|Scrapy|python-requests|node-fetch|HeadlessChrome/i;

export const useBotProtection = (): BotDetectionResult => {
  const [result, setResult] = useState<BotDetectionResult>({ 
    isBot: false, 
    isSuspicious: false,
    isReady: false 
  });

  useEffect(() => {
    const ua = navigator.userAgent || '';

    // Allowlisted crawlers short-circuit everything else.
    if (ALLOWED_CRAWLERS.test(ua)) {
      setResult({ isBot: false, isSuspicious: false, isReady: true });
      return;
    }

    const checks = {
      phantom: !!(window as any).callPhantom || !!(window as any)._phantom,
      trainingBot: TRAINING_BOTS.test(ua),
      noLanguages: !navigator.languages || navigator.languages.length === 0,
      suspiciousViewport: window.outerWidth === 0 || window.outerHeight === 0,
      webDriver: navigator.webdriver === true,
    };

    // Automation alone is no longer enough to hide content — it must look like a
    // harvesting client (headless/scripted UA, phantom shim, or no language set).
    const isBot =
      checks.phantom ||
      checks.trainingBot ||
      (checks.webDriver && (checks.noLanguages || checks.suspiciousViewport));

    const isSuspicious = checks.noLanguages || checks.suspiciousViewport || checks.webDriver;

    setResult({ isBot, isSuspicious, isReady: true });
  }, []);

  return result;
};
